import React, { useState, useEffect, useRef } from 'react';
import LaunchBanner from '../../components/LaunchBanner';

/**
 * NonprofitTechPartnership — /nonprofit-partners
 *
 * The white-paper positioning as a page: FHO as a companion layer deployed to
 * the people a nonprofit serves, sitting ALONGSIDE their case management
 * rather than claiming to replace it.
 *
 * LEAD CAPTURE: posts to /api/v1/quote-lead so submissions land in the
 * existing LeadsDashboard rather than creating a second inbox.
 *   - `count` must be a numeric string — the email builder calls int(count)
 *   - `quote` is required and must be a dict; type 'inquiry' routes through
 *     _quote_summary's final branch, which never touches count
 *   - partnershipType 'nonprofit' already has a colour chip in LeadsDashboard
 *
 * NOTE: a /nonprofit page already exists from the original twelve channels.
 * Compare the two and retire whichever loses — two nonprofit pages with
 * different pitches is worse than one.
 */

const NonprofitTechPartnership = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  const [form, setForm] = useState({
    orgName: '', contactName: '', title: '', email: '', count: '', programType: 'reentry'
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState('');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const PROGRAMS = [
    ['reentry', 'Reentry / justice-involved'],
    ['recovery', 'Addiction recovery'],
    ['workforce', 'Workforce development'],
    ['youth', 'Youth development'],
    ['housing', 'Housing / family stabilization'],
    ['aging', 'Aging services / senior living'],
    ['foundation', 'Foundation or endowment'],
    ['other', 'Other human services']
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setVisibleSections((p) => new Set([...p, e.target.id]));
      }),
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    Object.values(sectionRefs.current).forEach((r) => { if (r) observer.observe(r); });
    return () => observer.disconnect();
  }, []);

  const setSectionRef = (id) => (el) => { sectionRefs.current[id] = el; };
  const isVisible = (id) => visibleSections.has(id);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const fadeIn = (id, d = 0) => ({
    opacity: isVisible(id) ? 1 : 0,
    transform: isVisible(id) ? 'translateY(0)' : 'translateY(28px)',
    transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${d}s`
  });

  const T = {
    navy: '#1e3a5f', deep: '#14283f', ink: '#243a56', mute: '#5b7392',
    gold: '#c8922f', sand: '#faf7f1', pageBg: '#f7f9fc', line: '#e2e9f2', white: '#fff',
    serif: "Georgia, 'Times New Roman', serif",
    sans: "'Segoe UI', system-ui, -apple-system, sans-serif"
  };

  const wrap = { maxWidth: '1080px', margin: '0 auto', padding: '0 28px' };
  const section = { padding: '82px 0' };
  const eyebrow = { fontSize: '11px', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', color: T.gold, marginBottom: '14px' };
  const h2 = { fontFamily: T.serif, fontSize: 'clamp(26px,3.4vw,36px)', fontWeight: 700, color: T.navy, lineHeight: 1.18, margin: '0 0 18px' };
  const lead = { fontFamily: T.sans, fontSize: '17px', lineHeight: 1.65, color: T.ink, margin: '0 0 16px' };
  const body = { fontFamily: T.sans, fontSize: '15px', lineHeight: 1.62, color: T.mute, margin: 0 };
  const card = { background: T.white, border: `1px solid ${T.line}`, borderRadius: '14px', padding: '26px 28px', boxShadow: '0 1px 3px rgba(20,40,63,0.05)' };
  const label = { display: 'block', fontFamily: T.sans, fontSize: '12.5px', fontWeight: 700, color: T.navy, marginBottom: '6px', marginTop: '16px', letterSpacing: '0.2px' };
  const input = { width: '100%', padding: '12px 14px', borderRadius: '9px', border: `1px solid #cbd6e6`, fontFamily: T.sans, fontSize: '15px', color: T.ink, boxSizing: 'border-box', background: '#fff' };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.orgName.trim() || !form.contactName.trim() || !form.email.includes('@')) {
      setErrMsg('Organization, name, and a valid email are needed.');
      setStatus('error'); return;
    }
    setStatus('loading'); setErrMsg('');
    const label = (PROGRAMS.find(([v]) => v === form.programType) || [])[1] || 'Human services';
    try {
      const res = await fetch(window.location.origin + '/api/v1/quote-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgName: form.orgName.trim(),
          contactName: form.contactName.trim(),
          title: form.title.trim() || 'Not given',
          email: form.email.trim(),
          count: String(parseInt(form.count, 10) || 0),   // int(count) is called server-side
          partnershipType: 'nonprofit',
          segmentLabel: `Nonprofit — ${label}`,
          quote: {
            type: 'inquiry',
            tier: 'Companion layer — conversation requested',
            price: 'To be discussed',
            monthly: null
          }
        })
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setStatus('success');
    } catch (e) {
      setErrMsg('Something went wrong. Please email partnerships@freehappyone.com.');
      setStatus('error');
    }
  };

  const notThis = [
    ['Not a case management system', 'It holds no client records, no service episodes, no eligibility data, and no funder reporting. It does not replace Apricot, Casebook, ETO, Salesforce, or whatever you already run.'],
    ['Not clinical care', 'It does not diagnose, treat, or manage any condition, and makes no claim to. Where a conversation needs a clinician, its job is to say so.'],
    ['Not a crisis line', 'It recognises crisis language, responds with immediate human contact information, and does not attempt to manage the situation itself.'],
    ['Not an advocacy tool', 'When a participant raises a concern about their care or their treatment by others, it names what it heard and points toward a person who can act.']
  ];

  const fits = [
    ['Reentry and justice-involved', 'The weeks after release are the highest-risk window, and much of that risk sits in unstructured hours. Someone to talk to at eleven at night is someone more likely to make the morning appointment.'],
    ['Recovery programs', 'Recovery is built around the recognition that the dangerous hour is the unscheduled one. This occupies the moments before a person is ready to make the call — and points back toward the people who do the work.'],
    ['Workforce and youth', 'Discouragement between sessions is the common reason participants disengage. A rejection on Tuesday can end a placement a Thursday conversation would have saved — if they are still enrolled on Thursday.'],
    ['Housing and family stabilization', 'Participants carry instability continuously while contact with the program is periodic. This is for the continuous part.']
  ];

  const tiers = [
    ['Foundation', 'A single program or site, co-branded, with configuration support and a defined review process. Priced to be reachable on restricted funding.'],
    ['Growth', 'Multiple programs or sites in one organization, the full coach set, community-level anonymised themes, and regular review with our team.'],
    ['Systems', 'Foundations, endowments and agencies deploying across a portfolio, with cross-site aggregation of anonymised patterns and a shared evaluation design.']
  ];

  return (
    <div style={{ background: T.pageBg, minHeight: '100vh', fontFamily: T.sans }}>
      <LaunchBanner />

      {/* nav */}
      <div style={{ background: T.deep, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 28px' }}>
          <a href="/" style={{ fontFamily: T.serif, fontSize: '18px', fontWeight: 700, color: '#fff', textDecoration: 'none' }}>
            <span style={{ color: '#8b93f0' }}>F</span><span style={{ color: '#f87171' }}>H</span><span style={{ color: '#34d399' }}>O</span> Platform
          </a>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <a href="/" style={{ fontSize: '13.5px', color: '#c9d6ea', textDecoration: 'none', padding: '8px 14px' }}>Explore the platform</a>
            <button onClick={() => scrollTo('talk')} style={{
              padding: '9px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              background: T.gold, color: '#1a1508', fontFamily: T.sans, fontSize: '13.5px', fontWeight: 700
            }}>Start a conversation</button>
          </div>
        </div>
      </div>

      {/* hero */}
      <div style={{ background: `linear-gradient(168deg, ${T.deep} 0%, ${T.navy} 62%, #2d4a7a 100%)` }}>
        <div style={{ ...wrap, padding: '86px 28px 78px' }}>
          <div style={{ ...eyebrow, color: '#e0b45c' }}>For nonprofits, foundations &amp; human services</div>
          <h1 style={{ fontFamily: T.serif, fontSize: 'clamp(34px,5.2vw,52px)', fontWeight: 700, color: '#fff', lineHeight: 1.12, margin: '0 0 22px', maxWidth: '800px' }}>
            Support in the hours between sessions.
          </h1>
          <p style={{ fontSize: 'clamp(17px,2.1vw,20px)', lineHeight: 1.6, color: '#c9d6ea', margin: '0 0 34px', maxWidth: '680px' }}>
            A companion layer for the people you serve — available at any hour, from a phone,
            designed to hold a moment and hand the person back to your staff. It sits beside
            your case management system, not in place of it.
          </p>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <button onClick={() => scrollTo('talk')} style={{ padding: '15px 32px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: T.gold, color: '#1a1508', fontSize: '15px', fontWeight: 700 }}>Start a conversation</button>
            <a href="/" style={{ padding: '15px 32px', borderRadius: '10px', background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)', fontSize: '15px', fontWeight: 700, textDecoration: 'none' }}>See the platform</a>
          </div>
        </div>
      </div>

      {/* the gap */}
      <div id="gap" ref={setSectionRef('gap')} style={{ ...section, background: T.white }}>
        <div style={{ ...wrap, ...fadeIn('gap') }}>
          <div style={eyebrow}>The arithmetic every program lives with</div>
          <h2 style={h2}>One hour of skilled attention, in a week of one hundred and sixty-eight.</h2>
          <p style={{ ...lead, maxWidth: '780px' }}>
            Caseloads are set by funding. Funding is set by budget. No reasonable amount of either
            produces continuous human presence for every participant — so support is available when
            staff are available, and difficulty occurs when it occurs.
          </p>
          <div style={{ ...card, background: T.sand, borderLeft: `4px solid ${T.gold}`, marginTop: '28px', maxWidth: '840px' }}>
            <p style={{ ...lead, fontStyle: 'italic', margin: 0, fontFamily: T.serif }}>
              The question is not how to replace those hours of skilled human attention. It is what
              a person has access to in the many hours that surround them.
            </p>
          </div>
        </div>
      </div>

      {/* boundaries */}
      <div id="not" ref={setSectionRef('not')} style={{ ...section, background: T.pageBg }}>
        <div style={{ ...wrap, ...fadeIn('not') }}>
          <div style={eyebrow}>Being clear first</div>
          <h2 style={h2}>Four things this is not.</h2>
          <p style={{ ...lead, maxWidth: '740px' }}>
            Naming the boundaries early is more useful than discovering them in a procurement review.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: '18px', marginTop: '32px' }}>
            {notThis.map(([t, d], i) => (
              <div key={t} style={{ ...card, ...fadeIn('not', 0.07 * i) }}>
                <div style={{ fontFamily: T.serif, fontSize: '18px', fontWeight: 700, color: T.navy, marginBottom: '10px' }}>{t}</div>
                <p style={body}>{d}</p>
              </div>
            ))}
          </div>
          <div style={{ ...card, marginTop: '20px', borderLeft: `4px solid ${T.navy}` }}>
            <p style={{ ...lead, margin: 0 }}>
              Knowing where it ends is a designed feature. A companion that keeps counselling past
              the edge of its competence is more dangerous than one that stops.
            </p>
          </div>
        </div>
      </div>

      {/* fit */}
      <div id="fit" ref={setSectionRef('fit')} style={{ ...section, background: T.white }}>
        <div style={{ ...wrap, ...fadeIn('fit') }}>
          <div style={eyebrow}>Where it fits</div>
          <h2 style={h2}>Programs whose hardest hours fall outside program hours.</h2>
          <div style={{ marginTop: '28px' }}>
            {fits.map(([t, d], i) => (
              <div key={t} style={{ ...fadeIn('fit', 0.06 * i), display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '10px' : '22px', alignItems: 'flex-start',
                padding: '22px 4px', borderBottom: `1px solid ${T.line}` }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'baseline',
                  minWidth: isMobile ? 'auto' : '240px' }}>
                  <span style={{ fontFamily: T.serif, fontSize: '15px', fontWeight: 700, color: T.gold }}>{`0${i + 1}`}</span>
                  <span style={{ fontFamily: T.serif, fontSize: '18px', fontWeight: 700, color: T.navy }}>{t}</span>
                </div>
                <p style={{ ...body, flex: isMobile ? 'none' : 1,
                  paddingLeft: isMobile ? '34px' : 0 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* evidence */}
      <div id="evidence" ref={setSectionRef('evidence')} style={{ ...section, background: T.sand }}>
        <div style={{ ...wrap, ...fadeIn('evidence') }}>
          <div style={eyebrow}>Evidence</div>
          <h2 style={h2}>What we know, and what we do not.</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(330px,1fr))', gap: '20px', marginTop: '30px' }}>
            <div style={{ ...card, ...fadeIn('evidence', 0.05) }}>
              <div style={{ fontFamily: T.serif, fontSize: '19px', fontWeight: 700, color: T.navy, marginBottom: '12px' }}>What exists</div>
              <p style={{ ...body, marginBottom: '12px' }}>
                Live in production, with more than 3,200 coaching sessions delivered.
              </p>
              <p style={body}>
                Independently reviewed twice, without the reviewers knowing which product was ours.
                Both found that its handling of sensitive disclosures — checking a person&rsquo;s safety
                before offering guidance on a difficult relationship — met a standard a comparable
                product missed.
              </p>
            </div>
            <div style={{ ...card, ...fadeIn('evidence', 0.12), borderTop: `4px solid ${T.gold}` }}>
              <div style={{ fontFamily: T.serif, fontSize: '19px', fontWeight: 700, color: T.navy, marginBottom: '12px' }}>What does not</div>
              <p style={{ ...body, marginBottom: '12px' }}>
                There is no published outcome study. We have not run a controlled evaluation, and we
                do not claim measured improvement in any clinical or programmatic outcome.
              </p>
              <p style={{ ...body, fontStyle: 'italic' }}>
                If your organization requires that evidence before adopting a tool, you should not
                adopt this one yet. We would rather say so here than be asked for an instrument we
                do not have.
              </p>
            </div>
          </div>
          <div style={{ ...card, marginTop: '20px' }}>
            <div style={{ fontFamily: T.serif, fontSize: '19px', fontWeight: 700, color: T.navy, marginBottom: '10px' }}>How the evidence would be built</div>
            <p style={{ ...body, marginBottom: '12px' }}>
              A funded pilot with the evaluation designed before it begins: a validated instrument
              suited to your population, measurement before and after a defined period, a comparison
              condition where feasible, and an independent evaluator — a university partner or your
              own research staff — rather than the vendor.
            </p>
            <p style={body}>
              Social connection, engagement and retention among vulnerable populations are actively
              funded areas of philanthropy. Your organization is the eligible applicant; we serve as
              the named technology partner, help build the proposal, and stand up the pilot. You hold
              the grant and own the findings.
            </p>
          </div>
        </div>
      </div>

      {/* tiers */}
      <div id="tiers" ref={setSectionRef('tiers')} style={{ ...section, background: T.white }}>
        <div style={{ ...wrap, ...fadeIn('tiers') }}>
          <div style={eyebrow}>Partnership</div>
          <h2 style={h2}>Three ways in.</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '18px', marginTop: '30px' }}>
            {tiers.map(([t, d], i) => (
              <div key={t} style={{ ...card, ...fadeIn('tiers', 0.07 * i), background: i === 1 ? T.navy : T.white, border: i === 1 ? 'none' : `1px solid ${T.line}` }}>
                <div style={{ fontFamily: T.serif, fontSize: '21px', fontWeight: 700, color: i === 1 ? '#fff' : T.navy, marginBottom: '11px' }}>{t}</div>
                <p style={{ ...body, color: i === 1 ? '#c9d6ea' : T.mute }}>{d}</p>
              </div>
            ))}
          </div>
          <p style={{ ...body, marginTop: '22px', fontStyle: 'italic' }}>
            Pricing follows your model — per participant, per site, or grant-funded. We would rather
            understand how you structure your programs than propose a number into a vacuum.
          </p>
        </div>
      </div>

      {/* lead capture */}
      <div id="talk" ref={setSectionRef('talk')} style={{ background: T.deep, padding: '80px 0' }}>
        <div style={{ ...wrap, ...fadeIn('talk') }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: '44px', alignItems: 'start' }}>
            <div>
              <div style={{ ...eyebrow, color: '#e0b45c' }}>What we are asking for</div>
              <h2 style={{ ...h2, color: '#fff' }}>Not a purchase. A conversation.</h2>
              <p style={{ fontSize: '17px', lineHeight: 1.65, color: '#c9d6ea', margin: '0 0 20px' }}>
                Whether the hours between sessions are a real problem for your participants, and
                whether this is a reasonable thing to try. If the answer is no, that is a useful
                finding and we would rather have it early.
              </p>
              <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#8fa5c4' }}>
                Prefer email? <span style={{ color: '#e0b45c' }}>partnerships@freehappyone.com</span>
              </p>
              <a href="/" style={{ display: 'inline-block', marginTop: '18px', fontSize: '14px', color: '#c9d6ea' }}>
                Or explore the platform first &rarr;
              </a>
            </div>

            <div style={{ background: '#fff', borderRadius: '14px', padding: '30px 32px' }}>
              {status === 'success' ? (
                <div style={{ textAlign: 'center', padding: '26px 0' }}>
                  <div style={{ fontFamily: T.serif, fontSize: '22px', fontWeight: 700, color: T.navy, marginBottom: '10px' }}>Thank you.</div>
                  <p style={body}>
                    We read every one of these personally and will follow up. For anything urgent,
                    write to partnerships@freehappyone.com.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ fontFamily: T.serif, fontSize: '20px', fontWeight: 700, color: T.navy, marginBottom: '4px' }}>Tell us about your program</div>
                  <p style={{ ...body, fontSize: '13.5px' }}>No obligation, and no sales sequence.</p>

                  <label style={label}>Organization *</label>
                  <input style={input} value={form.orgName} onChange={set('orgName')} placeholder="Organization name" />

                  <label style={label}>Your name *</label>
                  <input style={input} value={form.contactName} onChange={set('contactName')} placeholder="First and last" />

                  <label style={label}>Role</label>
                  <input style={input} value={form.title} onChange={set('title')} placeholder="Executive Director, Program Director…" />

                  <label style={label}>Email *</label>
                  <input style={input} type="email" value={form.email} onChange={set('email')} placeholder="you@organization.org" />

                  <label style={label}>Program type</label>
                  <select style={input} value={form.programType} onChange={set('programType')}>
                    {PROGRAMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>

                  <label style={label}>People served each year (approximate)</label>
                  <input style={input} type="number" min="0" value={form.count} onChange={set('count')} placeholder="e.g. 250" />

                  <button onClick={submit} disabled={status === 'loading'} style={{
                    marginTop: '22px', width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
                    cursor: status === 'loading' ? 'wait' : 'pointer', background: T.navy, color: '#fff',
                    fontFamily: T.sans, fontSize: '15.5px', fontWeight: 700
                  }}>
                    {status === 'loading' ? 'Sending…' : 'Start the conversation'}
                  </button>

                  {status === 'error' && (
                    <p style={{ ...body, color: '#b91c1c', marginTop: '12px', fontSize: '13.5px' }}>{errMsg}</p>
                  )}
                  <p style={{ ...body, fontSize: '12.5px', marginTop: '14px' }}>
                    We review every enquiry personally. Your details are never shared or sold.
                  </p>
                </>
              )}
            </div>
          </div>

          <p style={{ fontSize: '13px', color: '#8296b5', marginTop: '40px', paddingTop: '22px', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            FHO Platform &nbsp;·&nbsp; MindStreams Technology, Thornbee LLC &nbsp;·&nbsp; freehappyone.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default NonprofitTechPartnership;
